import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';

export function Deploy() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [template, setTemplate] = useState<'dev' | 'ops' | 'raw'>('dev');
  const [deploying, setDeploying] = useState(false);
  const [provisioning, setProvisioning] = useState(false);
  const [countdown, setCountdown] = useState(90);
  const [error, setError] = useState('');
  const pollRef = useRef<number | undefined>(undefined);
  const countdownRef = useRef<number | undefined>(undefined);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearInterval(pollRef.current);
      clearInterval(countdownRef.current);
    };
  }, []);

  const handleDeploy = async () => {
    if (!name || name.length < 3 || deploying) return;
    setDeploying(true);
    setError('');
    try {
      const result = await api.deploy(name, template);
      localStorage.setItem('clz_api_key', result.apiKey);
      localStorage.setItem('clz_proxy_url', result.proxyUrl);

      // Start polling + countdown
      setProvisioning(true);
      setCountdown(90);

      countdownRef.current = window.setInterval(() => {
        setCountdown(prev => (prev > 0 ? prev - 1 : 0));
      }, 1000);

      pollRef.current = window.setInterval(async () => {
        try {
          const status = await api.getInstanceStatus();
          if (status.status === 'running') {
            clearInterval(pollRef.current);
            clearInterval(countdownRef.current);
            navigate('/chat');
          } else if (status.status === 'failed') {
            clearInterval(pollRef.current);
            clearInterval(countdownRef.current);
            setError('Provisioning failed. Try again with a different name.');
            setProvisioning(false);
            setDeploying(false);
          }
        } catch {
          // Ignore poll errors, keep trying
        }
      }, 5000);
    } catch (err: any) {
      if (err.message?.includes('already have an instance')) {
        // User already has an instance — check its status
        try {
          const status = await api.getInstanceStatus();
          if (status.status === 'running') {
            navigate('/chat');
          } else if (status.status === 'provisioning') {
            // Already provisioning — show the countdown
            setProvisioning(true);
            setCountdown(60);
            countdownRef.current = window.setInterval(() => {
              setCountdown(prev => (prev > 0 ? prev - 1 : 0));
            }, 1000);
            pollRef.current = window.setInterval(async () => {
              try {
                const s = await api.getInstanceStatus();
                if (s.status === 'running') {
                  clearInterval(pollRef.current);
                  clearInterval(countdownRef.current);
                  navigate('/chat');
                } else if (s.status === 'failed') {
                  clearInterval(pollRef.current);
                  clearInterval(countdownRef.current);
                  setError('Provisioning failed. Try again.');
                  setProvisioning(false);
                  setDeploying(false);
                }
              } catch {}
            }, 5000);
          } else {
            setError('Instance in unexpected state. Refresh and try again.');
            setDeploying(false);
          }
        } catch {
          navigate('/chat');
        }
        return;
      }
      setError(err.message || 'Deploy failed');
      setDeploying(false);
    }
  };

  if (provisioning) {
    const pct = Math.min(100, ((90 - countdown) / 90) * 100);
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-page)' }}>
        <div style={{ width: '100%', maxWidth: 440, padding: '2rem', textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🦞</div>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 22, color: 'var(--text-bright)', marginBottom: 8 }}>
            Deploying {name}...
          </h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 24 }}>
            Setting up your private OpenClaw instance
          </p>

          <div style={{ background: 'var(--bg-elevated)', borderRadius: 8, height: 8, overflow: 'hidden', marginBottom: 12 }}>
            <div style={{
              height: '100%', background: 'var(--green)', borderRadius: 8,
              width: `${pct}%`, transition: 'width 1s linear',
            }} />
          </div>

          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 32, color: 'var(--green)', marginBottom: 8 }}>
            {countdown > 0 ? `${countdown}s` : 'Almost there...'}
          </div>

          <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>
            {countdown > 70 ? 'Creating IAM role...' :
             countdown > 55 ? 'Setting up storage...' :
             countdown > 40 ? 'Configuring routing...' :
             countdown > 20 ? 'Starting container...' :
             countdown > 5 ? 'Registering endpoints...' :
             'Finalizing...'}
          </div>

          {error && <div style={{ color: 'var(--coral)', fontSize: 13, marginTop: 16 }}>{error}</div>}
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-page)' }}>
      <div style={{ width: '100%', maxWidth: 440, padding: '2rem' }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div style={{ fontSize: 48, marginBottom: 8 }}>🦞</div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 24, color: 'var(--text-bright)', marginBottom: 4 }}>Deploy an OpenClaw</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>Pick a name and template. Takes about 90 seconds.</p>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>Instance Name</label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
            placeholder="my-agent"
            disabled={deploying}
            style={{
              width: '100%', padding: '10px 14px', background: 'var(--bg-elevated)', border: '1px solid var(--border)',
              borderRadius: 8, color: 'var(--text-primary)', fontFamily: 'var(--font-mono)', fontSize: 14, outline: 'none',
              boxSizing: 'border-box',
            }}
          />
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>3-40 chars, lowercase + hyphens. Becomes your subdomain.</div>
        </div>

        <div style={{ marginBottom: 24 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>Template</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {([['dev', '10x Engineer'], ['ops', 'Ops Rockstar'], ['raw', 'Clean']] as const).map(([id, label]) => (
              <button
                key={id}
                onClick={() => setTemplate(id as any)}
                disabled={deploying}
                style={{
                  flex: 1, padding: '10px 8px', background: template === id ? 'var(--bg-elevated)' : 'transparent',
                  border: `1px solid ${template === id ? 'var(--coral)' : 'var(--border)'}`,
                  borderRadius: 8, color: template === id ? 'var(--text-bright)' : 'var(--text-secondary)',
                  fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-body)',
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {error && <div style={{ color: 'var(--coral)', fontSize: 13, marginBottom: 12 }}>{error}</div>}

        <button
          onClick={handleDeploy}
          disabled={!name || name.length < 3 || deploying}
          style={{
            width: '100%', padding: '12px 0', background: deploying ? 'var(--bg-elevated)' : 'var(--green)',
            border: 'none', borderRadius: 8, color: deploying ? 'var(--text-secondary)' : '#0a2e1c',
            fontSize: 15, fontWeight: 700, cursor: deploying ? 'default' : 'pointer', fontFamily: 'var(--font-body)',
            opacity: (!name || name.length < 3) ? 0.5 : 1,
          }}
        >
          {deploying ? 'Starting...' : 'Deploy'}
        </button>
      </div>
    </div>
  );
}
